import { ConfirmBanner } from '@/components/confirm-banner';
import { formatClock } from '@/shared/lib/format';
import type { ClaudeCodeConnectionData } from '@/shared/types/workspace';
import { Banner } from '@/shared/ui/banner';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { useClaudeCodeCard } from '../hooks/use-claude-code-card';
import { CLAUDE_STATUS } from '../model/connection-status';
import { AgentLogo } from './agent-logo';
import { ClaudeCodeActions } from './claude-code-actions';
import { ClaudeCodeDetails } from './claude-code-details';
import { ClaudeKeyForm } from './claude-key-form';

type ClaudeCodeCardProps = { connection: ClaudeCodeConnectionData };

/** Claude Code on the Connections page (design: Setup, every Claude state). */
export function ClaudeCodeCard({ connection }: ClaudeCodeCardProps) {
  const card = useClaudeCodeCard(connection);
  const status = CLAUDE_STATUS[connection.status];
  const rejectedAt =
    card.isRejected && connection.rejectedAt ? formatClock(connection.rejectedAt) : null;

  return (
    <>
      {card.isSaved ? (
        <div role="status">
          <Banner tone="ok" title="Key saved.">
            New Claude Code sessions use it. Running Pods were not touched.
          </Banner>
        </div>
      ) : null}
      <Card asChild>
        <article data-enter="" aria-labelledby="cc-h" className="flex flex-col gap-3 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <AgentLogo agent="claude-code" />
            <span className="flex flex-col leading-4.5">
              <span id="cc-h" className="font-semibold text-section">
                Claude Code
              </span>
              <span className="text-meta text-muted">
                Self-hosted environment, needs a Claude Team or Enterprise organization
              </span>
            </span>
            <Pill tone={status.tone} hasDot className="ml-auto">
              {status.label}
            </Pill>
          </div>
          {rejectedAt ? (
            <div role="alert">
              <Banner tone="bad" title={`Claude rejected this key at ${rejectedAt}.`}>
                New Claude Code sessions cannot get Pods until you replace it. It may have been
                revoked in your Claude organization.
              </Banner>
            </div>
          ) : null}
          {card.isOff ? null : <ClaudeCodeDetails connection={connection} />}
          {card.mode === 'form' ? (
            <ClaudeKeyForm
              mode={card.isOff ? 'connect' : 'replace'}
              onSaved={card.onSaved}
              onCancel={card.closeForm}
            />
          ) : null}
          {card.mode === 'confirm' ? (
            <ConfirmBanner
              id="dc-h"
              title="Disconnect Claude Code?"
              keepLabel="Keep connected"
              confirmLabel="Disconnect"
              isConfirming={card.isDisconnecting}
              onKeep={card.keepConnected}
              onConfirm={() => void card.disconnect()}
            >
              New Claude Code sessions will not get Pods. Running Pods finish their sessions. You
              can connect again with a new key.
            </ConfirmBanner>
          ) : null}
          {card.isOff && card.mode === 'view' ? (
            <p className="text-muted">
              Create a self-hosted environment in your Claude organization and paste its key. Keys
              are stored encrypted and never written into Pod images.
            </p>
          ) : null}
          {card.mode === 'view' ? (
            <ClaudeCodeActions
              isOff={card.isOff}
              isRejected={card.isRejected}
              onOpenForm={card.openForm}
              onAskDisconnect={card.askDisconnect}
            />
          ) : null}
        </article>
      </Card>
    </>
  );
}
